/**
 * AI-credits wallet store — a small, shell-wide source of truth for the
 * tenant's balance, shared by the header badge ({@link AiCreditsBadge}) and the
 * global refill host ({@link CreditGuardHost}).
 *
 * Kept separate from the heavier settings-store (which also loads the ledger +
 * usage) so the header widget can show a live balance without pulling the whole
 * settings surface. `refresh()` hits `/api/v1/credits/balance`, which
 * auto-provisions the wallet + welcome grant server-side, and is safe to call
 * on mount, on a poll, and after any AI action.
 */
import { create } from 'zustand';

import { getBalance, type Wallet } from '@/lib/api/credits';

/** Window event any component can fire to ask the badge/host to re-fetch. */
export const AI_CREDITS_UPDATED_EVENT = 'psn:ai-credits-updated';

/** Fire-and-forget: ask the credits store to refresh (e.g. after an AI run). */
export function requestCreditsRefresh(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(AI_CREDITS_UPDATED_EVENT));
  } catch {
    /* ignore */
  }
}

interface CreditsState {
  wallet: Wallet | null;
  loading: boolean;
  loaded: boolean;
  /** Guards against overlapping in-flight refreshes. */
  _inflight: boolean;
  refresh: (opts?: { force?: boolean }) => Promise<void>;
  /** Low balance = available at or below the wallet's own alert threshold. */
  isLow: () => boolean;
}

export const useCreditsStore = create<CreditsState>((set, get) => ({
  wallet: null,
  loading: false,
  loaded: false,
  _inflight: false,

  refresh: async (opts) => {
    if (get()._inflight && !opts?.force) return;
    set({ _inflight: true, loading: !get().loaded });
    try {
      const wallet = await getBalance();
      set({ wallet, loaded: true });
    } catch {
      // The wallet service may be unreachable (a fresh env / outage). Degrade
      // silently — the badge hides and the guard host still shows the 402 modal
      // from the event payload, which carries required/available on its own.
      set({ loaded: true });
    } finally {
      set({ _inflight: false, loading: false });
    }
  },

  isLow: () => {
    const w = get().wallet;
    if (!w) return false;
    const threshold = w.low_balance_threshold ?? 0;
    return w.available <= threshold;
  },
}));
